import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Building2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

// Public login: Contractor and Vendor only. ScapeSource staff sign in on
// the separate /admin page (pages/AdminLogin.jsx).
const PORTAL_META = {
  contractor: { label: "Contractor", cta: "Sign in as Contractor", home: "/contractor" },
  vendor: { label: "Vendor", cta: "Sign in as Vendor", home: "/vendor" },
};

export default function Login() {
  // "Join the network free" in a purchase-order email arrives as
  // /login?join=vendor&claim=…&email=…&name=… — straight to the vendor signup,
  // prefilled, and the claim ties it to the company that PO was addressed to.
  const [params] = useSearchParams();
  const joining = params.get("join") === "vendor";
  const claimToken = joining ? params.get("claim") : null;
  const [portal, setPortal] = useState(joining ? "vendor" : "contractor");
  const [email, setEmail] = useState(joining ? params.get("email") || "" : "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSignup, setShowSignup] = useState(joining);
  const [companyName, setCompanyName] = useState(joining ? params.get("name") || "" : "");
  const [adminName, setAdminName] = useState("");
  const [category, setCategory] = useState("");
  const { login, registerCompany, registerVendor } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password, portal);
      navigate(PORTAL_META[user.portal].home);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = portal === "vendor"
        ? await registerVendor({ company_name: companyName, admin_name: adminName, email, password, category: category || null, claim_token: claimToken })
        : await registerCompany({ company_name: companyName, admin_name: adminName, email, password });
      navigate(PORTAL_META[user.portal].home);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-10 py-16">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2 text-slate-900 font-bold text-[16px] mb-10">
          <Building2 size={20} className="text-sky-500" />
          ScapeSource
        </div>

        {!showSignup ? (
          <>
            <h1 className="text-[26px] font-extrabold tracking-tight text-slate-900">Welcome back</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">Sign in to your workspace</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[13px] font-medium text-slate-700">Work email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="login-email-input"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[13px] font-medium text-slate-700">Password</label>
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[12px] text-sky-600 cursor-pointer">
                    Forgot?
                  </Link>
                </div>
                <div className="relative mt-1.5">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    className="w-full h-11 pl-3.5 pr-10 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                    className="absolute right-0 top-0 h-11 w-10 flex items-center justify-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold text-[14px] transition-colors disabled:opacity-60"
              >
                {loading ? "Signing in…" : PORTAL_META[portal].cta}
              </button>
            </form>

            {(portal === "contractor" || portal === "vendor") && (
              <div className="text-[13px] text-slate-500 mt-4">
                New to ScapeSource?{" "}
                <button onClick={() => setShowSignup(true)} data-testid="create-company-link" className="text-sky-600 font-medium">
                  {portal === "vendor" ? "Register your business" : "Create a company account"}
                </button>
              </div>
            )}

            <div className="mt-7 pt-5 border-t border-slate-100">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-2.5">Switch portal</div>
              <div className="space-y-2">
                {Object.entries(PORTAL_META)
                  .filter(([k]) => k !== portal)
                  .map(([k, meta]) => (
                    <button
                      key={k}
                      onClick={() => setPortal(k)}
                      data-testid={`switch-portal-${k}`}
                      className="w-full h-11 rounded-lg border border-slate-200 text-[13.5px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      Sign in as {meta.label}
                    </button>
                  ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-[26px] font-extrabold tracking-tight text-slate-900">
              {portal === "vendor" ? "Join the vendor network" : "Create your company"}
            </h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">
              {portal === "vendor"
                ? (claimToken
                  ? "A contractor sent you a purchase order through ScapeSource. Create your free vendor account to see it and respond — a ScapeSource team member reviews every new vendor first."
                  : "Set up your vendor account — a ScapeSource team member reviews every new vendor before it goes live to contractors.")
                : "Set up your contractor workspace"}
            </p>
            <form onSubmit={submitSignup} className="space-y-4">
              <div>
                <label className="text-[13px] font-medium text-slate-700">{portal === "vendor" ? "Business name" : "Company name"}</label>
                <input required value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="signup-company-input" className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
              </div>
              {portal === "vendor" && (
                <div>
                  <label className="text-[13px] font-medium text-slate-700">Category <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input placeholder="e.g. Landscape Supply, Building Materials" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="signup-category-input" className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
                </div>
              )}
              <div>
                <label className="text-[13px] font-medium text-slate-700">Your name</label>
                <input required value={adminName} onChange={(e) => setAdminName(e.target.value)} data-testid="signup-name-input" className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
              </div>
              <div>
                <label className="text-[13px] font-medium text-slate-700">Work email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email-input" className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
              </div>
              <div>
                <label className="text-[13px] font-medium text-slate-700">Password</label>
                <div className="relative mt-1.5">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="signup-password-input"
                    className="w-full h-11 pl-3.5 pr-10 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="signup-password-toggle"
                    className="absolute right-0 top-0 h-11 w-10 flex items-center justify-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              {error && <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="signup-error">{error}</div>}
              <button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold text-[14px] transition-colors disabled:opacity-60">
                {loading ? "Creating…" : portal === "vendor" ? "Create vendor account" : "Create company account"}
              </button>
            </form>
            <button onClick={() => setShowSignup(false)} data-testid="back-to-login-link" className="text-[13px] text-sky-600 font-medium mt-4">
              ← Back to sign in
            </button>
          </>
        )}
      </div>
      </div>

      <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center px-16" style={{ background: "linear-gradient(135deg, #38bdf8, #0369a1)" }}>
        <div className="max-w-md">
          <p className="text-white text-2xl font-semibold leading-snug">
            Every dollar committed on a job gets an owner, an approval, and a paper trail — before it hits QuickBooks.
          </p>
          <p className="text-sky-100 text-sm mt-5">ScapeSource — procurement control for contractors</p>
        </div>
      </div>
    </div>
  );
}
